import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Shield, FileText, LogOut, Check } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const LegalAcceptance = () => {
  const { token, logout, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [privacyContent, setPrivacyContent] = useState([]);
  const [termsContent, setTermsContent] = useState([]);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [contentLoading, setContentLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [isReady, setIsReady] = useState(false);

  // Wait for auth to be fully ready before fetching content
  useEffect(() => {
    if (!authLoading && token && user) {
      setIsReady(true);
    }
  }, [authLoading, token, user]);

  // Fetch legal content only when auth is ready
  useEffect(() => {
    if (isReady) {
      fetchLegalContent();
    }
  }, [isReady]);

  const fetchLegalContent = async () => {
    try {
      setContentLoading(true);
      const [privacyRes, termsRes] = await Promise.all([
        axios.get(`${API}/api/legal/privacy-policy`),
        axios.get(`${API}/api/legal/terms-conditions`)
      ]);
      setPrivacyContent(privacyRes.data.content || []);
      setTermsContent(termsRes.data.content || []);
    } catch (error) {
      console.error('Error fetching legal content:', error);
      toast.error('Errore nel caricamento dei documenti legali');
    } finally {
      setContentLoading(false);
    }
  };

  const handleAccept = async () => {
    if (!privacyAccepted || !termsAccepted) {
      toast.error('Devi accettare entrambi i documenti per procedere');
      return;
    }

    try {
      setSubmitting(true);
      await axios.post(
        `${API}/api/legal/accept`,
        { accept_privacy: true, accept_terms: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Accettazione registrata');
      // Small delay to ensure DB is updated before redirect
      await new Promise(resolve => setTimeout(resolve, 500));
      // Use window.location to force full page reload and fresh acceptance check
      window.location.href = '/dashboard';
    } catch (error) {
      console.error('Error accepting legal documents:', error);
      toast.error('Errore nella registrazione dell\'accettazione');
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const renderContent = (content) => {
    if (!content || content.length === 0) {
      return <p className="text-slate-500 italic">Contenuto non disponibile</p>;
    }

    return content.map((block, index) => {
      switch (block.type) {
        case 'heading':
          return (
            <h3 key={index} className="text-base font-semibold text-slate-800 mt-4 mb-2 first:mt-0">
              {block.content}
            </h3>
          );
        case 'paragraph':
          return (
            <p key={index} className="text-sm text-slate-600 mb-3 leading-relaxed whitespace-pre-line">
              {block.content}
            </p>
          );
        case 'list':
          return (
            <ul key={index} className="list-disc list-outside text-sm text-slate-600 mb-3 space-y-1 ml-5">
              {block.content.split('\n').map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          );
        case 'note':
          return (
            <p key={index} className="text-xs text-slate-500 italic mb-3">
              {block.content}
            </p>
          );
        default:
          return (
            <p key={index} className="text-sm text-slate-600 mb-3">
              {block.content}
            </p>
          );
      }
    });
  };

  // Show loading while auth is loading, not ready, or content is loading
  if (authLoading || !isReady || contentLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#5941F5] mx-auto mb-4"></div>
          <p className="text-slate-500 text-sm">Caricamento in corso...</p>
        </div>
      </div>
    );
  }

  // If no user after loading, redirect to login
  if (!user) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-slate-500">Sessione non valida. Effettua il login.</p>
          <Button onClick={() => navigate('/login')} className="mt-4">
            Vai al Login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-[#5941F5] to-[#3BA99C] rounded-lg flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-slate-800">e-CAA</h1>
              <p className="text-xs text-slate-500">Accettazione Documenti Legali</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-600">
              Accesso come: <strong>{user?.email}</strong>
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              className="text-slate-600 hover:text-slate-800"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Esci
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-6 py-8">
        <div className="text-center mb-8">
          <h2 className="text-xl font-bold text-slate-800 mb-2">
            Benvenuto! Prima di continuare...
          </h2>
          <p className="text-slate-600">
            Per utilizzare la piattaforma è necessario leggere e accettare i seguenti documenti.
          </p>
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Privacy Policy */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="bg-[#5941F5] px-5 py-3 flex items-center gap-2">
              <Shield className="w-5 h-5 text-white" />
              <h3 className="font-semibold text-white">Privacy Policy</h3>
            </div>
            <div className="p-5 h-[450px] overflow-y-auto border-b border-slate-100">
              {renderContent(privacyContent)}
            </div>
            <div className="p-4 bg-slate-50">
              <button
                type="button"
                onClick={() => setPrivacyAccepted(!privacyAccepted)}
                className={`w-full flex items-center gap-3 p-3 rounded-lg border-2 transition-all ${
                  privacyAccepted 
                    ? 'bg-emerald-50 border-emerald-400 text-emerald-800' 
                    : 'bg-white border-slate-300 text-slate-700 hover:border-slate-400'
                }`}
              >
                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                  privacyAccepted ? 'bg-emerald-500 border-emerald-500' : 'border-slate-400'
                }`}>
                  {privacyAccepted && <Check className="w-3 h-3 text-white" />}
                </div>
                <span className="font-medium text-sm">
                  Ho letto e accetto la Privacy Policy
                </span>
              </button>
            </div>
          </div>

          {/* Terms and Conditions */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="bg-[#3BA99C] px-5 py-3 flex items-center gap-2">
              <FileText className="w-5 h-5 text-white" />
              <h3 className="font-semibold text-white">Termini e Condizioni</h3>
            </div>
            <div className="p-5 h-[450px] overflow-y-auto border-b border-slate-100">
              {renderContent(termsContent)}
            </div>
            <div className="p-4 bg-slate-50">
              <button
                type="button"
                onClick={() => setTermsAccepted(!termsAccepted)}
                className={`w-full flex items-center gap-3 p-3 rounded-lg border-2 transition-all ${
                  termsAccepted 
                    ? 'bg-emerald-50 border-emerald-400 text-emerald-800' 
                    : 'bg-white border-slate-300 text-slate-700 hover:border-slate-400'
                }`}
              >
                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                  termsAccepted ? 'bg-emerald-500 border-emerald-500' : 'border-slate-400'
                }`}>
                  {termsAccepted && <Check className="w-3 h-3 text-white" />}
                </div>
                <span className="font-medium text-sm">
                  Ho letto e accetto i Termini e Condizioni
                </span>
              </button>
            </div>
          </div>
        </div>

        {/* Submit Section */}
        <div className="text-center">
          <Button
            onClick={handleAccept}
            disabled={!privacyAccepted || !termsAccepted || submitting}
            size="lg"
            className="bg-[#5941F5] hover:bg-[#4835D4] text-white px-10 py-6 text-base disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Elaborazione...
              </>
            ) : (
              'Accetto e Proseguo'
            )}
          </Button>
          
          {(!privacyAccepted || !termsAccepted) && (
            <p className="text-sm text-slate-500 mt-3">
              Seleziona entrambe le caselle per procedere
            </p>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 py-4 mt-8">
        <p className="text-center text-xs text-slate-400">
          © 2026 e-CAA - Etom Srl.
        </p>
      </footer>
    </div>
  );
};

export default LegalAcceptance;
